// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuthService, AuthProvider } from '@whitelabel/auth';
import { createSessionService } from '@whitelabel/session';

import WebPlayer, { loadPlaybackState, clearPlaybackState, getStateAge } from "@whitelabel/web-player";
import DefaultHeader from "./components/Header";
import PopoutPlayerRoute from "./components/PopoutPlayerRoute";
import PageRenderer from "./components/PageRenderer";
import ApplyPage from "./components/onboarding/ApplyPage";
import InvitePage from "./components/onboarding/InvitePage";
import SetupPage from "./components/onboarding/SetupPage";
import "./App.css";

import { resolveTenant, loadTenantConfig, applyTenantConfigToCSS } from "@whitelabel/utils";

// Create session service
const sessionService = createSessionService(process.env.REACT_APP_TENANT || 'shaqfu');
//import MainApp from './MainApp';  // assume your App.js renders <MainApp>

console.log("[App.js] bootstrapping…");

// Immediately load tenant config once, before React mounts
(function bootstrapTenantConfig() {
  const tenant = resolveTenant();
  console.log(`[App] loading config for tenant="${tenant}"…`);
  loadTenantConfig(tenant)
    .then((cfg) => {
      // stash everything for JS to read
      window.__TENANT_CFG__ = cfg;

      // Apply tenant configuration to CSS using shared utility
      applyTenantConfigToCSS(cfg);
    })
    .catch((err) => {
      console.error("[App] config load error:", err);
      document.body.innerHTML = "<h1>Failed to load config</h1>";
    });
})();

// —————————————————————————————————————————————————————————————————————————————
// NavigationHandler: Listens for 'navigateTo' events and uses React Router
// This enables SPA navigation from puck components without full page reloads
// —————————————————————————————————————————————————————————————————————————————
const NavigationHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleNavigateTo = (e) => {
      const { url } = e.detail;
      if (url) {
        console.log('[NavigationHandler] SPA navigation to:', url);
        // Use React Router navigate for internal URLs
        navigate(url);
      }
    };

    window.addEventListener('navigateTo', handleNavigateTo);
    return () => window.removeEventListener('navigateTo', handleNavigateTo);
  }, [navigate]);

  return null; // This component doesn't render anything
};

// —————————————————————————————————————————————————————————————————————————————
// Onboarding routes are public pages, not part of a station.
//
// The WebPlayer deliberately lives OUTSIDE <Routes> so playback survives
// navigation, which means it would otherwise render on the application and
// invitation pages too — a station's player bar sitting on the page where
// somebody is asking to become a station. This gate keeps it off them without
// disturbing how it persists everywhere else.
// —————————————————————————————————————————————————————————————————————————————
const ONBOARDING_PREFIX = "/onboarding";

const HideOnOnboarding = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname === ONBOARDING_PREFIX || pathname.startsWith(`${ONBOARDING_PREFIX}/`)) {
    return null;
  }
  return children;
};

// —————————————————————————————————————————————————————————————————————————————
// MainApp: renders Header + PageRenderer (no player - player is outside Routes)
// —————————————————————————————————————————————————————————————————————————————
const MainApp = ({ slug = null }) => {
  const tenant = resolveTenant();

  // annotate <body data-tenant="…">
  useEffect(() => {
    document.body.setAttribute("data-tenant", tenant);

    // Update document title with tenant name
    // Use tenant name from config if available, otherwise capitalize tenant ID
    const config = window.__TENANT_CFG__;
    const tenantDisplayName = config?.TENANT_NAME ||
                             config?.name ||
                             tenant.charAt(0).toUpperCase() + tenant.slice(1);
    document.title = `${tenantDisplayName} Radio`;
  }, [tenant]);

  return (
    <>
      <DefaultHeader />
      <div className="app-container">
        <PageRenderer slug={slug} />
        <div style={{ height: 1, opacity: 0 }} />
      </div>
    </>
  );
};

// —————————————————————————————————————————————————————————————————————————————
// AuthenticatedApp: session‐init + routing with Auth0 integration
// WebPlayer is rendered here (outside Routes) so it persists across navigation
// —————————————————————————————————————————————————————————————————————————————
function AuthenticatedApp() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading, user, getToken } = useAuthService();

  // Audio state - lifted here so it persists across route changes
  const [streamUrlOverride, setStreamUrlOverride] = useState(null);
  const [metadataOverride, setMetadataOverride] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(null); // { title, artist, position, url, cover }
  const tenant = resolveTenant();

  // Check for resumable playback state on mount (only show once)
  useEffect(() => {
    const savedState = loadPlaybackState();
    if (savedState && !savedState.isLive && savedState.position > 0) {
      console.log('[App] Found resumable playback state:', savedState.title);
      setResumePrompt({
        title: savedState.title,
        artist: savedState.artist,
        position: savedState.position,
        url: savedState.url,
        cover: savedState.cover,
        duration: savedState.duration,
        age: getStateAge()
      });
      // Clear immediately so it only shows once
      clearPlaybackState();
    }
  }, []);

  // Handle resume playback
  const handleResume = () => {
    if (resumePrompt) {
      console.log('[App] Resuming playback:', resumePrompt.title, 'at', resumePrompt.position);
      setStreamUrlOverride(resumePrompt.url);
      setMetadataOverride({
        title: resumePrompt.title,
        artist: resumePrompt.artist,
        cover: resumePrompt.cover
      });
      // Dispatch event to seek to saved position after player loads
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forcePlay'));
        window.dispatchEvent(new CustomEvent('seekTo', { detail: { position: resumePrompt.position } }));
      }, 500);
      setResumePrompt(null);
      clearPlaybackState();
    }
  };

  // Handle dismiss resume prompt
  const handleDismissResume = () => {
    console.log('[App] Resume dismissed');
    setResumePrompt(null);
    clearPlaybackState();
  };

  // Force session refresh on every mount and auth change
  useEffect(() => {
    const bootstrapSession = async (forceRefresh = false) => {
      try {
        let sessionId = localStorage.getItem("sessionId");
        console.log("[Session] localStorage:", sessionId, "forceRefresh:", forceRefresh);

        // Force clear cache if requested
        if (forceRefresh && sessionId) {
          console.log("[Session] Forcing refresh of session data");
        }

        // fallback to cookie
        if (!sessionId) {
          const m = document.cookie.match(/(^| )sessionId=([^;]+)/);
          sessionId = m?.[2] ?? null;
          if (sessionId) {
            localStorage.setItem("sessionId", sessionId);
            console.log("[Session] recovered from cookie");
          }
        }

        // create if still missing
        if (!sessionId) {
          const res = await fetch(
            "/api/usermanagement/sessions/create-anonymous-session",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                data: { ip: "127.0.0.1", preferences: {} },
              }),
            }
          );
          sessionId = res.headers.get("x-session-token");
          if (sessionId) {
            localStorage.setItem("sessionId", sessionId);
            console.log("[Session] new session created");
          }
        }

        // ALWAYS refetch to get latest user details - no caching
        // Add cache-busting timestamp to force fresh data
        // Build headers with optional Auth0 token
        const headers = {
          "Content-Type": "application/json",
          "x-session-token": sessionId,
          "Cache-Control": "no-cache",
          "Pragma": "no-cache"
        };

        // Add Auth0 token if user is authenticated
        console.log("[Session] Auth check before token:", {
          isAuthenticated,
          isLoading,
          hasGetToken: typeof getToken === 'function'
        });

        if (isAuthenticated && !isLoading) {
          try {
            console.log("[Session] Attempting to get Auth0 token...");
            const token = await getToken();

            if (token) {
              headers["Authorization"] = `Bearer ${token}`;
              console.log("[Session] ✅ Auth0 token added to headers");
            } else {
              console.log("[Session] ⚠️ getToken returned null");
            }
          } catch (tokenError) {
            console.error("[Session] ❌ Error getting Auth0 token:", tokenError);
          }
        } else {
          console.log("[Session] Skipping token - user not authenticated or still loading");
        }

        const check = await fetch(
          `/api/usermanagement/sessions/check-session/${sessionId}?t=${Date.now()}`,
          { headers }
        );
        const data = await check.json();
        console.log("[Session] Response from check-session:", data);

        // Handle new session creation if conflict was detected
        if (data.newSessionCreated) {
          console.log("[Session] New session created due to conflict, updating localStorage");
          localStorage.setItem("sessionId", data.sessionId);
          // Update sessionId for next requests
          sessionId = data.sessionId;
        }

        if (data.valid || data.success) {
          setSession(data.session);
          console.log("[Session] Session set with user:", {
            sessionId,
            userId: data.session?.userId,
            user: data.session?.user
          });

          // Log warning if session doesn't have user object when it should
          if (data.session?.userId && !data.session?.user) {
            console.warn("[Session] ⚠️ Session has userId but missing user object - backend needs to fetch user details");
          }
        } else {
          console.warn("[Session] invalid, but proceeding");
          setSession(data.session || null);
        }

        // Auto-migrate anonymous session when user is authenticated
        if (isAuthenticated && !isLoading) {
          const anonymousSessionId = sessionService.getPendingMigrationId();
          if (anonymousSessionId) {
            // Simple migration - just clear the anonymous session
            sessionService.clearMigrationData();
            console.log('✅ Anonymous session migrated');
            // Force refresh after migration to get updated session with user
            setTimeout(() => bootstrapSession(true), 1000);
          }
        }
      } catch (err) {
        console.error("[Session] error:", err);
      } finally {
        setLoading(false);
      }
    };

    bootstrapSession();

    // Also refresh session every 30 seconds to ensure fresh data
    const interval = setInterval(() => {
      console.log("[Session] Periodic refresh");
      bootstrapSession(true);
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // ✅ Listen for 'playAudio' events from DjMixesCard (persists across navigation)
  useEffect(() => {
    const handlePlayAudio = (e) => {
      const { url, title, artist, cover } = e.detail;
      if (url) {
        console.log(`[AuthenticatedApp] Playing audio override: ${title}`, url);
        setStreamUrlOverride(url);

        // Set metadata override for the player
        setMetadataOverride({
          title: title || 'DJ Mix',
          artist: artist || 'Shaq Fu Radio',
          cover: cover || window.__TENANT_CFG__?.LOGO_URL || ''
        });

        // Dispatch event to force player to start playing
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('forcePlay'));
        }, 300);
      }
    };

    window.addEventListener('playAudio', handlePlayAudio);
    return () => window.removeEventListener('playAudio', handlePlayAudio);
  }, []);

  // ✅ Listen for 'backToLive' event to return to live stream
  useEffect(() => {
    const handleBackToLive = () => {
      console.log('[AuthenticatedApp] ✅ Back to Live - resetting stream');
      setStreamUrlOverride(null);
      setMetadataOverride(null);
      window.__CURRENT_PLAYING_URL__ = null;
    };

    window.addEventListener('backToLive', handleBackToLive);
    return () => window.removeEventListener('backToLive', handleBackToLive);
  }, []);

  if (loading || isLoading) return <div>Loading…</div>;

  // Enhanced session with Auth0 user data
  const enhancedSession = {
    ...session,
    auth0User: isAuthenticated ? user : null,
    isAuthenticated,
    getToken
  };

  // Format time for display in resume prompt
  const formatResumeTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Router>
      {/* NavigationHandler enables SPA navigation from puck components */}
      <NavigationHandler />

      {/* Resume Playback Prompt */}
      {resumePrompt && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          border: '1px solid #444',
          borderRadius: '12px',
          padding: '16px 20px',
          zIndex: 2000,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          maxWidth: '400px',
          width: '90%',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            {resumePrompt.cover && (
              <img
                src={resumePrompt.cover}
                alt=""
                style={{ width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover' }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {resumePrompt.title}
              </div>
              <div style={{ color: '#999', fontSize: '12px' }}>
                {resumePrompt.artist} • {formatResumeTime(resumePrompt.position)} / {formatResumeTime(resumePrompt.duration)}
              </div>
              <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                Paused {resumePrompt.age}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleResume}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: 'var(--player-accent, #dd3333)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              ▶ Continue Playing
            </button>
            <button
              onClick={handleDismissResume}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: '#888',
                border: '1px solid #444',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* WebPlayer rendered outside Routes - persists across navigation */}
      <HideOnOnboarding>
        <WebPlayer
          session={enhancedSession}
          config={window.__TENANT_CFG__ || {}}
          streamUrl={streamUrlOverride || window.__TENANT_CFG__?.STREAM_URL}
          metadataUrl={`/api/contentmanagement/tenants/${tenant}/metadata`}
          metadataOverride={metadataOverride}
          showMinimizeButton={true}
          showPopoutButton={true}
        />
      </HideOnOnboarding>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/popout-player" element={<PopoutPlayerRoute />} />
        {/* Public onboarding. These must be declared before the /:slug
            catch-all is *read* by a human; react-router ranks by specificity
            so the order does not affect matching. */}
        <Route path="/onboarding/apply" element={<ApplyPage />} />
        <Route path="/onboarding/invite/:token" element={<InvitePage />} />
        {/* Where a station lands after setting their password. Auth0's New
            Universal Login ignores our result_url and offers the application's
            own login route instead, so this must also work with no station id
            in the path. */}
        <Route path="/onboarding/setup/:tenantId" element={<SetupPage />} />
        <Route path="/onboarding/setup" element={<SetupPage />} />
        <Route path="/:slug" element={<SlugPage />} />
      </Routes>
    </Router>
  );
}

// Wrapper component to extract slug from URL params
const SlugPage = () => {
  const { slug } = useParams();
  return <MainApp slug={slug} />;
};

// —————————————————————————————————————————————————————————————————————————————
// App: Auth0 Provider wrapper
// —————————————————————————————————————————————————————————————————————————————
function App() {
  return (
    <AuthProvider 
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      audience={process.env.REACT_APP_AUTH0_AUDIENCE}
    >
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;
